import {
  Button,
  Col,
  Switch,
  Form,
  Input,
  Row,
  Select,
  DatePicker,
  notification,
  Checkbox,
} from "antd";
import dayjs from "dayjs";
import { useState } from "react";
import { businessType } from "../../../constants/typeOfEntity";
import useStore from "../../../hooks/useStore";
import postOnboardForm from "../../apiRepository/postOnboardForm";
import DoordashDialogConfirm from "./DoordashDialogConfirm";
import styles from "../../deliveryServices/DeliveryServices.module.css";
import DoordashDialogCancel from "./DoordashDialogCancel";
import { CheckCircleFilled } from "@ant-design/icons";

const dateFormatList = ["MM/DD/YYYY", "MM/DD/YY", "MM-DD-YYYY", "MM-DD-YY"];

export default function DoordashOnBoardingForm({
  closeForm,
  onSuccess,
  deliveryService,
}) {
  const { store, setStore } = useStore();
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [isExisting, setIsExisting] = useState(false);
  const [apiErrors, setApiErrors] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formValues, setFormValues] = useState(null);
  const [hasFormError, setHasFormError] = useState(false);

  const openNotification = (type) => {
    notification.open({
      message: <strong>{`${type} Request Submitted`}</strong>,
      description: `Your form has been successfully submitted.`,
      placement: "top",
      icon: <CheckCircleFilled style={{ color: "#52c41a" }} />,
    });
  };

  const existingAccountToggle = (checked) => {
    setIsExisting(checked);
  };

  const handleCloseModal = () => {
    setShowCancelModal(false);
    setShowConfirmModal(false);
    setApiErrors(null);
    setIsSubmitting(false);
  };

  const submit = async () => {
    try {
      const data = { ...formValues };
      data.dob = dayjs(data.dob).format("MM/DD/YYYY");
      data.storeId = store?.storeId;

      delete data.isExisting;

      setIsSubmitting(true);
      await postOnboardForm({
        token: store?.token,
        integratorId: store?.integratorId,
        storeId: store?.storeId,
        deliveryService,
        isExisting,
        data,
      });

      setIsSubmitting(false);

      openNotification("Onboarding");
      onSuccess();
    } catch (err) {
      setIsSubmitting(false);
      const { errors } = err?.response?.data || {};
      console.log(err?.response || err.message);

      setApiErrors(errors || err.message);
    }
  };

  const handleConfirm = (data) => {
    setFormValues(data);
    setShowConfirmModal(true);
  };

  return (
    <>
      <div style={{ padding: "30px 80px" }}>
        <div style={{ marginBottom: "30px" }}>
          <p style={{ fontWeight: 700, marginBottom: "5px" }}>
            Connect to DoorDash
          </p>
          <p className={styles.dsFormTitle2}>
            Fill out form with your details as the store contact person.
            Processing may take up to 24 hours or longer.
          </p>
        </div>
        <Form
          autoComplete="off"
          onFinish={handleConfirm}
          onFinishFailed={() => setHasFormError(true)}
          layout="vertical"
          initialValues={{
            storeId: store?.storeId,
            firstName: store?.formData?.contactFirstName,
            lastName: store?.formData?.contactLastName,
            email: store?.formData?.legalEmail || store?.email,
            phone: store?.formData?.phone || store?.phone,
            taxRate:
              store?.taxRate || store?.salesTax || store?.formData?.taxRate,
            legalBusinessName: store?.formData?.legalBusinessName,
            entity: store?.formData?.typeOfEntity,
            dob: dayjs(store?.formData?.legalDOB),
            accountNumber: store?.formData?.bankAccountNumber,
            routingNumber: store?.formData?.routingNumber,
            ssn: store?.formData?.ssn,
            ein: store?.formData?.ein,
            carriesCbd: store?.formData?.carriesCbd || false,
            carriesOralNicotine: store?.formData?.carriesOralNicotine || false,
          }}
        >
          <Row gutter={30}>
            <Col span={12}>
              <Form.Item
                label={<span className={styles.dsFormLabels}>First Name</span>}
                name="firstName"
                required
                rules={[{ required: true, message: "First name is required." }]}
              >
                <Input />
              </Form.Item>

              <Form.Item
                label={<span className={styles.dsFormLabels}>Email</span>}
                name="email"
                required
                rules={[
                  { required: true, message: "Email is required." },
                  { type: "email", message: "Invalid email." },
                ]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label={<span className={styles.dsFormLabels}>Last Name</span>}
                name="lastName"
                required
                rules={[{ required: true, message: "Last name is required." }]}
              >
                <Input />
              </Form.Item>
              <Form.Item
                label={
                  <span className={styles.dsFormLabels}>Phone Number</span>
                }
                name="phone"
                required
                rules={[
                  { required: true, message: "Phone is required." },
                  {
                    pattern:
                      /^(\+1|1)?\s*[-.\s]?\(?[2-9]{1}[0-9]{2}\)?[-.\s]?[2-9]{1}[0-9]{2}[-.\s]?[0-9]{4}$/,
                    message: "Invalid US phone number.",
                  },
                ]}
              >
                <Input />
              </Form.Item>
            </Col>
          </Row>
          <Row>
            <Col span={24}>
              <p className={styles.dsFormText}>
                The fields below are required by DoorDash to streamline your
                store's onboarding experience
              </p>
            </Col>
          </Row>
          <Row>
            <Col span={24}>
              <Form.Item
                label={
                  <span className={styles.dsFormLabels}>
                    Legal Business Name
                  </span>
                }
                name="legalBusinessName"
                required
                rules={[
                  { required: true, message: "Business Name is required." },
                ]}
              >
                <Input />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={30}>
            <Col span={8}>
              <Form.Item
                label={<span className={styles.dsFormLabels}>Entity Type</span>}
                name="entity"
                required
                rules={[
                  { required: true, message: "Entity Type is required." },
                ]}
              >
                <Select options={businessType} placeholder="Please select" />
              </Form.Item>
              <Form.Item
                label={
                  <span className={styles.dsFormLabels}>Account Number</span>
                }
                name="accountNumber"
                required
                rules={[
                  { required: true, message: "Account Number is required." },
                ]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label={
                  <span className={styles.dsFormLabels}>Routing Number</span>
                }
                name="routingNumber"
                required
                rules={[
                  { required: true, message: "Routing Number is required." },
                ]}
              >
                <Input />
              </Form.Item>
              <Form.Item
                label={
                  <span className={styles.dsFormLabels}>Date of Birth</span>
                }
                name="dob"
                required
                rules={[
                  { required: true, message: "Date of birth is required." },
                ]}
              >
                <DatePicker
                  defaultValue={dayjs("01/01/2015", dateFormatList[0])}
                  format={dateFormatList}
                  style={{ width: "100%" }}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label={
                  <span className={styles.dsFormLabels}>
                    Employer ID Number
                  </span>
                }
                name="ein"
                required
                rules={[
                  {
                    required: true,
                    message: "EIN is required.",
                  },
                  {
                    pattern: /^[0-9]+$/,
                    message: "Please enter a number",
                  },
                ]}
              >
                <Input maxLength={10} placeholder="123456789" />
              </Form.Item>
              <Form.Item
                label={
                  <span className={styles.dsFormLabels}>
                    Last 4 Digits of Owner's SSN
                  </span>
                }
                name="ssn"
                required
                rules={[
                  {
                    required: true,
                    message: "Social Security Number is required.",
                  },
                  {
                    pattern: /^\d{4}$/,
                    message: "SSN must be 4 digits.",
                  },
                ]}
              >
                <Input maxLength={4} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={30}>
            <Col span={8}>
              <Form.Item
                label={
                  <span className={styles.dsFormLabels}>Store Tax (%)</span>
                }
                name="taxRate"
                required
                rules={[
                  { required: true, message: "This is a required field." },
                  {
                    validator: (_, value) => {
                      if (value && +value > 100) {
                        return Promise.reject(
                          "Please enter a value that is 100 or less.",
                        );
                      }

                      if (value && +value <= 0) {
                        return Promise.reject(
                          "Please enter a value that is greater than 0",
                        );
                      }

                      if (value && String(value).trim() === ".") {
                        return Promise.reject(
                          "Please enter a value that is greater than 0",
                        );
                      }

                      return Promise.resolve();
                    },
                  },
                ]}
                normalize={(value) => {
                  if (!value) return value;
                  const cleaned = value
                    .replace(/[^0-9.]/g, "")
                    .replace(/(\..*?)\..*/g, "$1");
                  let truncated = cleaned.slice(0, cleaned.indexOf(".") + 4);
                  return truncated;
                }}
              >
                <Input suffix={"%"} maxLength={6} />
              </Form.Item>
            </Col>
            <Col span={16} style={{ display: "flex", alignItems: "center" }}>
              <Form.Item name="isExisting" valuePropName="checked" noStyle>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    backgroundColor: "#FFF",
                    padding: "2px 8px",
                    width: "100%",
                  }}
                >
                  <div style={{ display: "flex", flexDirection: "column" }}>
                    <span
                      className={styles.dsFormLabels}
                      style={{ fontSize: "11px", opacity: ".30" }}
                    >
                      IMPORTANT
                    </span>
                    <span style={{ fontSize: "12px", opacity: ".65" }}>
                      {`DOES YOUR STORE CURRENTLY HAVE A ${deliveryService} ACCOUNT?`}
                    </span>
                  </div>
                  <Switch
                    checkedChildren="Yes"
                    unCheckedChildren="No"
                    onChange={existingAccountToggle}
                  />
                </div>
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={30} style={{ marginBottom: "12px" }}>
            <Col span={8}></Col>
            <Col span={16} style={{ display: "flex", alignItems: "center" }}>
              <div style={{ marginBottom: 16, width: "100%" }}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "2px 8px",
                    backgroundColor: "#FFF",
                  }}
                >
                  <span className={styles.dsFormLabels}>
                    DOES YOUR STORE CARRY:
                  </span>
                  <Form.Item
                    name="carriesCbd"
                    valuePropName="checked"
                    style={{ marginBottom: 0 }}
                  >
                    <Checkbox>
                      <span className={styles.dsFormLabels}>CBD</span>
                    </Checkbox>
                  </Form.Item>
                  <Form.Item
                    name="carriesOralNicotine"
                    valuePropName="checked"
                    style={{ marginBottom: 0 }}
                  >
                    <Checkbox>
                      <span className={styles.dsFormLabels}>ORAL NICOTINE</span>
                    </Checkbox>
                  </Form.Item>
                </div>
              </div>
            </Col>
          </Row>
          <Row justify="space-between">
            <Col>
              <Button
                type="default"
                danger
                onClick={() => setShowCancelModal(true)}
                className={styles.dsButtonCancel}
              >
                <p>Cancel</p>
              </Button>
            </Col>
            <Col
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                gap: 20,
              }}
            >
              {hasFormError ? (
                <p className={styles.dsFormErrorText}>
                  Please check the fields marked in red and try again.
                </p>
              ) : (
                ""
              )}
              <Button htmlType="submit" className={styles.dsButtonSubmit}>
                <p>Submit</p>
              </Button>
            </Col>
          </Row>
        </Form>
      </div>
      <DoordashDialogConfirm
        handleSubmit={submit}
        handleCloseModal={handleCloseModal}
        isSubmitting={isSubmitting}
        showModal={showConfirmModal}
        errors={apiErrors}
        clearError={() => setApiErrors(null)}
      />
      <DoordashDialogCancel
        closeForm={closeForm}
        handleCloseModal={handleCloseModal}
        showModal={showCancelModal}
      />
    </>
  );
}
